import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Store, ShoppingCart, Package, Wallet, MessageCircle, FileSpreadsheet,
  ScanBarcode, Plug, Check, ArrowRight, Phone, Mail, Menu, X,
  TrendingUp, ShieldCheck, Zap, BarChart3, Star, Quote,
} from 'lucide-react';
import { B2BonixLogo } from '@/components/B2BonixLogo';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '@/components/ui/accordion';

const NAVY = '#1a224f';
const NAVY_DARK = '#12173a';
const GREEN = '#10b981';
const GREEN_DARK = '#059669';
const WHATSAPP_PHONE = 'https://wa.me/902166069924?text=' + encodeURIComponent('Merhaba, B2Bonix B2B paneli hakkında bilgi almak istiyorum.');

const NAV_LINKS = [
  { id: 'ozellikler', label: 'Özellikler' },
  { id: 'nasil-calisir', label: 'Nasıl Çalışır' },
  { id: 'yorumlar', label: 'Yorumlar' },
  { id: 'fiyatlandirma', label: 'Fiyatlandırma' },
  { id: 'sss', label: 'SSS' },
];

const FEATURES = [
  { icon: Store, title: 'Bayi Yönetimi', desc: 'Bayilerinizi bölge, iskonto ve personel bazında tek panelden yönetin.' },
  { icon: ShoppingCart, title: 'Sipariş Yönetimi', desc: 'Siparişleri durum bazında takip edin, PDF/Excel çıktısı alın.' },
  { icon: Package, title: 'Ürün Kataloğu', desc: 'Barkodlu ürün kataloğu, stok ve fiyat yönetimi.' },
  { icon: Wallet, title: 'Cari / Hesap Takibi', desc: 'Borç-alacak hareketleri, bakiye ve cari ekstre takibi.' },
  { icon: MessageCircle, title: 'WhatsApp Bildirimleri', desc: 'Sipariş ve tahsilat bildirimlerini otomatik gönderin.' },
  { icon: FileSpreadsheet, title: 'PDF / Excel Raporlama', desc: 'Sipariş ve cari hareketleri tek tıkla dışa aktarın.' },
  { icon: ScanBarcode, title: 'Barkod Sorgulama', desc: 'Ürünleri barkod ile anında sorgulayın.' },
  { icon: Plug, title: 'Entegrasyon Desteği', desc: 'Muhasebe ve ödeme altyapınız için özel entegrasyon.' },
];

const STEPS = [
  { n: '01', title: 'Kurulum & Entegrasyon', desc: 'Kullandığınız muhasebe ve ödeme altyapınızı belirtin, ekibimiz size özel entegrasyonu kursun.' },
  { n: '02', title: 'Bayi Tanımlama', desc: 'Bayilerinizi sisteme ekleyin, özel iskonto ve fiyat grupları oluşturun.' },
  { n: '03', title: 'Ürün & Fiyat', desc: 'Ürün kataloğunuzu ve fiyat listelerinizi yükleyin, stokları yönetin.' },
  { n: '04', title: 'Satışa Başlayın', desc: 'Siparişler aksın, tahsilatlar otomatikleşsin, raporlar elinizin altında olsun.' },
];

const TESTIMONIALS = [
  { text: 'Bayilerimiz artık 7/24 sipariş verebiliyor. Telefon trafiğimiz azaldı, sipariş hatası neredeyse sıfırlandı.', name: 'Ahmet Y.', role: 'Satış Müdürü, ABC Dağıtım' },
  { text: 'Cari hesap takibi ve WhatsApp hatırlatmaları sayesinde tahsilat süremiz belirgin şekilde kısaldı.', name: 'Fatma K.', role: 'İşletme Sahibi, FK Ticaret' },
  { text: 'Personellerime bayi ataması yapabiliyorum, herkes kendi bölgesinden sorumlu. İş akışımız çok düzeldi.', name: 'Mehmet D.', role: 'Muhasebe Sorumlusu, Demir Ltd.' },
];

const PLANS = [
  { name: 'Başlangıç', tagline: 'Küçük ekipler için', features: ['Bayi yönetimi', 'Sipariş yönetimi', 'Ürün kataloğu', 'Temel raporlama', 'E-posta desteği'] },
  { name: 'Profesyonel', tagline: 'Büyüyen işletmeler için', popular: true, features: ['Tüm Başlangıç özellikleri', 'WhatsApp bildirimleri', 'Cari / hesap takibi', 'PDF / Excel raporlama', 'Öncelikli destek'] },
  { name: 'Kurumsal', tagline: 'Tam kapsamlı çözüm', features: ['Tüm Profesyonel özellikleri', 'Muhasebe entegrasyonu', 'Ödeme altyapısı entegrasyonu', 'Özel geliştirme', '7/24 öncelikli destek'] },
];

const FAQS = [
  { q: 'B2Bonix nasıl kurulur?', a: 'Kurulum tamamen bize ait. İhtiyaçlarınızı belirledikten sonra panelinizi hazırlar, bayi ve ürün verilerinizi aktarmanıza yardımcı oluruz.' },
  { q: 'Muhasebe programıma bağlanabilir mi?', a: 'Evet. Panel içindeki Entegrasyonlar sayfasından kullandığınız muhasebe programını belirtip destek kaydı oluşturmanız yeterli; ekibimiz size özel entegrasyon API’sini geliştirir.' },
  { q: 'Online ödeme alabilir miyim?', a: 'Evet. Kullanmak istediğiniz ödeme altyapısını (sanal POS / ödeme kuruluşu) Entegrasyonlar sayfasından belirtin, ekibimiz bağlantıyı sizin için kursun.' },
  { q: 'Bayilerim için özel fiyat tanımlayabilir miyim?', a: 'Kesinlikle. Her bayi veya bayi grubu için özel iskonto oranları ve fiyat listeleri tanımlayabilirsiniz.' },
  { q: 'Mobil cihazlardan kullanılabilir mi?', a: 'Evet, B2Bonix tamamen mobil uyumludur. Bayileriniz telefon ve tabletten sorunsuz sipariş verebilir.' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.45, delay: i * 0.06, ease: 'easeOut' } }),
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-white font-body text-slate-800" data-testid="landing-page">
      {/* ===== Header ===== */}
      <header className={`sticky top-0 z-50 transition-all ${scrolled ? 'bg-white/90 backdrop-blur-xl border-b border-slate-200/70 shadow-sm' : 'bg-white border-b border-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <B2BonixLogo variant="dark" iconSize={36} />
          <nav className="hidden md:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              <button key={l.id} onClick={() => scrollTo(l.id)} className="text-sm font-medium text-slate-600 hover:text-[#1a224f] transition-colors" data-testid={`nav-${l.id}`}>
                {l.label}
              </button>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <button onClick={() => navigate('/login')} className="text-sm font-semibold text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-100 transition-colors" data-testid="header-login-btn">
              Giriş Yap
            </button>
            <button onClick={() => scrollTo('iletisim')} className="inline-flex items-center gap-1.5 text-sm font-semibold text-white px-5 py-2.5 rounded-lg transition-all hover:opacity-90" style={{ backgroundColor: NAVY }} data-testid="header-contact-btn">
              İletişime Geçin
            </button>
          </div>
          <button className="md:hidden p-2 text-slate-800" onClick={() => setMobileOpen((v) => !v)} data-testid="mobile-menu-btn">
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white px-4 py-4 space-y-1">
            {NAV_LINKS.map((l) => (
              <button key={l.id} onClick={() => scrollTo(l.id)} className="block w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">{l.label}</button>
            ))}
            <div className="pt-2 flex gap-2">
              <button onClick={() => navigate('/login')} className="flex-1 py-2.5 rounded-lg border border-slate-300 text-sm font-semibold" data-testid="mobile-login-btn">Giriş Yap</button>
              <button onClick={() => scrollTo('iletisim')} className="flex-1 py-2.5 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: NAVY }}>İletişime Geçin</button>
            </div>
          </div>
        )}
      </header>

      {/* ===== Hero ===== */}
      <section className="relative overflow-hidden bg-slate-50/60">
        <div className="absolute inset-0 -z-10 opacity-60" style={{ backgroundImage: 'linear-gradient(to right, rgba(26,34,79,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(26,34,79,0.04) 1px, transparent 1px)', backgroundSize: '46px 46px', maskImage: 'radial-gradient(ellipse 70% 60% at 50% 0%, black 35%, transparent 75%)' }} />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-20 md:pt-28 md:pb-24 text-center">
          <motion.span variants={fadeUp} initial="hidden" animate="show" className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold mb-7 border" style={{ backgroundColor: 'rgba(16,185,129,0.1)', color: GREEN_DARK, borderColor: 'rgba(16,185,129,0.25)' }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: GREEN }} />
            Yeni Nesil B2B Bayi Paneli
          </motion.span>
          <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1} className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-slate-900">
            B2B Süreçlerinizi <span style={{ color: GREEN }}>Tek Panelden</span> Yönetin
          </motion.h1>
          <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2} className="mt-6 text-base md:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Bayi yönetimi, siparişler, cari takibi ve raporlama tek platformda. B2Bonix ile işletmenizi dijitalleştirin, satışlarınızı katlayın.
          </motion.p>
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => scrollTo('iletisim')} className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg text-white font-semibold transition-all hover:opacity-90" style={{ backgroundColor: NAVY }} data-testid="hero-demo-btn">
              Özel B2B Tasarlat
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button onClick={() => navigate('/login')} className="inline-flex items-center justify-center px-7 py-3.5 rounded-lg font-semibold border-2 border-[#1a224f] text-[#1a224f] transition-colors hover:bg-[#1a224f] hover:text-white" data-testid="hero-login-btn">
              Demo Görüntüle
            </button>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4} className="mt-7 flex flex-wrap gap-x-7 gap-y-2 justify-center text-sm text-slate-500">
            {['Kolay Kurulum', 'Gelişmiş Raporlama', '7/24 Destek'].map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5"><Check className="h-4 w-4" style={{ color: GREEN }} /> {t}</span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== Stats Band ===== */}
      <section style={{ backgroundColor: NAVY }} className="text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-y-8">
          {[
            { value: '49+', label: 'Aktif Firma' },
            { value: '%40', label: 'Satış Artışı' },
            { value: '50.000+', label: 'İşlem Hacmi' },
            { value: '7/24', label: 'Teknik Destek' },
          ].map((s, i) => (
            <div key={s.label} className={`text-center ${i < 3 ? 'md:border-r md:border-white/10' : ''}`}>
              <div className="font-display font-extrabold text-3xl md:text-4xl" style={{ color: GREEN }}>{s.value}</div>
              <div className="mt-1 text-sm text-white/70">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== Features ===== */}
      <section id="ozellikler" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-sm font-semibold" style={{ color: GREEN_DARK }}>Özellikler</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl mt-2 tracking-tight text-slate-900">Tüm İhtiyaçlarınız Tek Platformda</h2>
            <p className="mt-3 text-slate-600">Bayi yönetiminden tahsilata, raporlamadan entegrasyona kadar ihtiyacınız olan her şey.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map((f, i) => (
              <motion.div key={f.title} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} custom={i % 4}
                className="group bg-white rounded-xl border border-slate-200 p-6 hover:border-[#1a224f]/30 hover:shadow-lg transition-all duration-300" data-testid={`feature-card-${i}`}>
                <div className="h-11 w-11 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(16,185,129,0.1)' }}>
                  <f.icon className="h-5 w-5" style={{ color: GREEN_DARK }} />
                </div>
                <h3 className="font-display font-bold text-base text-slate-900">{f.title}</h3>
                <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== How it works ===== */}
      <section id="nasil-calisir" className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-sm font-semibold" style={{ color: GREEN_DARK }}>Nasıl Çalışır</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl mt-2 tracking-tight text-slate-900">4 Adımda Dijitalleşin</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <motion.div key={s.n} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="relative rounded-xl border border-slate-200 p-6 bg-white">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg font-display font-extrabold text-white text-lg" style={{ backgroundColor: NAVY }}>{s.n}</span>
                <h3 className="font-display font-bold text-lg mt-4 text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Testimonials ===== */}
      <section id="yorumlar" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-sm font-semibold" style={{ color: GREEN_DARK }}>Yorumlar</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl mt-2 tracking-tight text-slate-900">Kullanıcılarımız Ne Diyor?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="rounded-xl border border-slate-200 bg-white p-7">
                <Quote className="h-7 w-7 mb-3" style={{ color: GREEN }} />
                <p className="text-slate-700 leading-relaxed">{t.text}</p>
                <div className="mt-5 flex items-center gap-3">
                  <span className="h-10 w-10 rounded-full flex items-center justify-center font-bold text-white text-sm" style={{ backgroundColor: NAVY }}>{t.name.charAt(0)}</span>
                  <div>
                    <div className="font-semibold text-slate-900 text-sm">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Pricing ===== */}
      <section id="fiyatlandirma" className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-sm font-semibold" style={{ color: GREEN_DARK }}>Fiyatlandırma</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl mt-2 tracking-tight text-slate-900">İşletmenize Uygun Paket</h2>
            <p className="mt-3 text-slate-600">İhtiyaçlarınıza göre özel teklif hazırlayalım. Tüm paketler özel geliştirmeye açıktır.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            {PLANS.map((p) => (
              <div key={p.name} className={`relative rounded-2xl bg-white p-7 transition-all hover:shadow-xl ${p.popular ? 'border-2 shadow-lg md:-translate-y-2' : 'border border-slate-200'}`} style={p.popular ? { borderColor: NAVY } : {}} data-testid={`plan-${p.name}`}>
                {p.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1" style={{ backgroundColor: NAVY }}>
                    <Star className="h-3 w-3 fill-white" /> En Popüler
                  </span>
                )}
                <h3 className="font-display font-bold text-xl text-slate-900">{p.name}</h3>
                <p className="text-sm text-slate-500 mt-1">{p.tagline}</p>
                <ul className="mt-6 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: GREEN }} />
                      <span className="text-slate-600">{f}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => scrollTo('iletisim')} className={`mt-7 w-full py-3 rounded-lg font-semibold text-sm transition-all ${p.popular ? 'text-white hover:opacity-90' : 'border-2 border-[#1a224f] text-[#1a224f] hover:bg-[#1a224f] hover:text-white'}`} style={p.popular ? { backgroundColor: NAVY } : {}} data-testid={`plan-cta-${p.name}`}>
                  Teklif Alın
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section id="sss" className="py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-sm font-semibold" style={{ color: GREEN_DARK }}>SSS</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl mt-2 tracking-tight text-slate-900">Sıkça Sorulan Sorular</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border border-slate-200 rounded-xl px-5 bg-white" data-testid={`faq-${i}`}>
                <AccordionTrigger className="text-left font-display font-semibold text-slate-900 hover:no-underline">{item.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ===== Contact / CTA ===== */}
      <section id="iletisim" className="relative py-20 md:py-28 overflow-hidden" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DARK} 100%)` }}>
        <div className="absolute inset-0 opacity-[0.1]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '28px 28px' }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="font-display font-extrabold text-3xl md:text-5xl tracking-tight">Hemen Başlayın</h2>
          <p className="mt-4 text-white/80 max-w-2xl mx-auto md:text-lg">B2B özel geliştirme ve panel hakkında detaylı bilgi almak için bizimle iletişime geçin.</p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <a href={WHATSAPP_PHONE} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-4 rounded-xl bg-white p-5 text-left transition-transform hover:-translate-y-1" data-testid="contact-phone">
              <span className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: 'rgba(16,185,129,0.12)' }}><Phone className="h-5 w-5" style={{ color: GREEN_DARK }} /></span>
              <span><span className="block text-xs text-slate-500">WhatsApp / Telefon</span><span className="block font-display font-bold text-slate-900">+90 216 606 99 24</span></span>
            </a>
            <a href="mailto:info@webplace.tr" className="group flex items-center gap-4 rounded-xl bg-white p-5 text-left transition-transform hover:-translate-y-1" data-testid="contact-email">
              <span className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: 'rgba(26,34,79,0.1)' }}><Mail className="h-5 w-5" style={{ color: NAVY }} /></span>
              <span><span className="block text-xs text-slate-500">E-posta</span><span className="block font-display font-bold text-slate-900">info@webplace.tr</span></span>
            </a>
          </div>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer style={{ backgroundColor: NAVY_DARK }} className="text-white/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <B2BonixLogo variant="light" iconSize={34} />
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            {NAV_LINKS.map((l) => (
              <button key={l.id} onClick={() => scrollTo(l.id)} className="hover:text-white transition-colors">{l.label}</button>
            ))}
          </div>
          <p className="text-xs text-white/50">&copy; {new Date().getFullYear()} B2Bonix by WebPlace. Tüm hakları saklıdır.</p>
        </div>
      </footer>
    </div>
  );
}
